/**
 * Draws a scene (buildScene) and nothing else: every position, path, label and
 * knot comes from the scene; nothing here measures the DOM or computes a
 * place. The verification surface for ?scene=1 on the v2 map — no editing.
 *
 *   behind the cards  lane bands, associations (thin curves), loop arcs
 *   in front          causal connectors (thick, orthogonal), cut stubs
 *   on top            cards, then labels and knots
 */
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { BookOpen, Bot, User, Flag, Milestone, HelpCircle } from 'lucide-react';
import PathwayCanvas from '../components/PathwayCanvas';
import { markerIdFor } from '../components/PathwayArrow';
import { MILESTONE_STATUS_COLOURS } from '../components/routeNodeMark';
import { EFFECT_TYPE_COLORS } from '../../config/effectTypes';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import API_ENDPOINTS from '../../config/api';
import { buildScene } from './buildScene';
import { cardTimes, reportRounds } from './timeAxis';
import TimelineAxis, { AXIS_HEIGHT } from './TimelineAxis';

const INK = '#0b0d12';
const CARD_FILL = '#1b1f2a';
const COLOURS = {
  mission: '#ffffff', start: '#4cc7ce', goal: '#f0b35e', done: '#4cc7ce', milestone: '#a78bfa',
  blocker: '#EF5350', avoider: '#4caf50', wanted: '#4caf50', unwanted: '#e94b6e', neutral: '#5b6478',
  endState: '#ffffff', question: '#7dd3fc',
};
const SOURCE_ICONS = { documents: BookOpen, ai: Bot, person: User };
const SOLID = new Set(['mission', 'start', 'goal']);

// The label key: a shared target or the end state above them, else the card's kind
const kindKey = (card) => {
  if (card.level === 2) return 'target';
  if (card.level === 3) return 'endState';
  if (card.milestone) return 'milestone';
  return card.kind === 'node' ? (card.nodeKind || 'consequence') : card.kind;
};

function cardColour(card) {
  if (card.level === 3) return COLOURS.endState;
  if (card.nodeKind === 'question') return COLOURS.question;
  if (card.milestone) return COLOURS.milestone;
  if (COLOURS[card.kind] && card.kind !== 'node') return COLOURS[card.kind];
  if (card.valence === 'wanted') return COLOURS.wanted;
  if (card.valence === 'unwanted') return COLOURS.unwanted;
  return COLOURS.neutral;
}

const edgeColour = (e) => (e.kind === 'structural' ? '#5b6478' : (EFFECT_TYPE_COLORS[e.type] || EFFECT_TYPE_COLORS.leads_to));

const CardText = styled.div`
  box-sizing: border-box; width: 100%; height: 100%; padding: 8px 12px; pointer-events: none; user-select: none;
  font-family: inherit; color: ${(p) => p.ink}; overflow: hidden;
  .head { display: flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 700;
    letter-spacing: 0.06em; text-transform: uppercase; opacity: 0.85; margin-bottom: 4px; }
  .chip { margin-left: auto; padding: 1px 7px; border-radius: 8px; border: 1px solid; font-size: 9px; }
  .body { font-size: 14px; line-height: 18px; font-weight: ${(p) => (p.solid ? 700 : 500)}; }
`;

function SceneCard({ card, t, onHover, onClick, lit }) {
  const colour = cardColour(card);
  const solid = SOLID.has(card.kind);
  const Icon = SOURCE_ICONS[card.source];
  const ink = solid ? INK : '#e8ebf2';
  const kindLabel = t(`impactPath.scene.kind.${kindKey(card)}`);
  const question = card.nodeKind === 'question';
  const status = card.milestone?.status;
  const text = card.kind === 'done' ? t('impactPath.scene.doneGoals', { count: card.goals?.length || 0 }) : card.text;
  // A proposal the room has not decided is drawn dashed and lighter; a softened one lighter.
  const proposed = card.state === 'proposed';
  const dashed = card.kind === 'done' || question || proposed;
  return (
    <g
      className={`scene-card scene-card-${card.kind}${proposed ? ' scene-card-proposed' : ''}`}
      data-card-id={card.id}
      opacity={proposed ? 0.78 : (card.state === 'softened' ? 0.88 : 1)}
      style={onClick ? { cursor: 'pointer' } : undefined}
      onMouseEnter={onHover && (() => onHover(card.id))}
      onMouseLeave={onHover && (() => onHover(null))}
      onClick={onClick && ((ev) => { ev.stopPropagation(); onClick(card); })}
    >
      {card.kind === 'mission' && (
        <text x={card.x} y={card.y - 14} fill={COLOURS.start} fontSize={26} fontWeight={800} letterSpacing="0.08em">
          {t('impactPath.scene.kind.mission').toUpperCase()}
        </text>
      )}
      <rect
        x={card.x} y={card.y} width={card.w} height={card.h} rx={10}
        fill={solid ? colour : CARD_FILL} stroke={lit ? '#ffffff' : (solid ? '#ffffff' : colour)}
        strokeWidth={lit || card.milestone || card.kind === 'done' || card.level ? 3 : 2}
        strokeDasharray={dashed ? '8 5' : undefined}
      />
      {/* New since the room last saw the map: a dot at the card's corner. */}
      {card.fresh && <circle cx={card.x + card.w - 8} cy={card.y + 8} r={5} fill={COLOURS.start} stroke={INK} strokeWidth={1.5} />}
      {/* The card's HTML takes no pointer events: wheel and drag land on the
          SVG rect beneath, as on the live map's cards. WebKit hit-tests
          foreignObject content wrongly inside a transformed group. */}
      <foreignObject x={card.x} y={card.y} width={card.w} height={card.h} pointerEvents="none" style={{ pointerEvents: 'none' }}>
        <CardText ink={ink} solid={solid}>
          <div className="head">
            {Icon && <Icon size={12} strokeWidth={2.2} color={ink} />}
            {card.milestone && <Milestone size={12} strokeWidth={2.4} color={COLOURS.milestone} />}
            {card.kind === 'goal' && <Flag size={12} strokeWidth={2.4} color={INK} />}
            {question && <HelpCircle size={12} strokeWidth={2.4} color={COLOURS.question} />}
            <span>{kindLabel}</span>
            {status && MILESTONE_STATUS_COLOURS[status] && (
              <span className="chip" style={{ color: MILESTONE_STATUS_COLOURS[status], borderColor: MILESTONE_STATUS_COLOURS[status] }}>
                {t(`impactPath.route.status.${status}`)}
              </span>
            )}
          </div>
          <div className="body">{text}</div>
        </CardText>
      </foreignObject>
    </g>
  );
}

function EdgeLabel({ point, text, colour, onClick }) {
  const w = Math.max(40, text.length * 7 + 16);
  return (
    <g
      pointerEvents={onClick ? 'auto' : 'none'}
      style={onClick ? { cursor: 'pointer' } : undefined}
      transform={`translate(${point.x}, ${point.y})`}
      onClick={onClick && ((ev) => { ev.stopPropagation(); onClick({ x: ev.clientX, y: ev.clientY }); })}
    >
      <rect x={-w / 2} y={-10} width={w} height={20} rx={10} fill={CARD_FILL} stroke={colour} strokeWidth={1.2} />
      <text textAnchor="middle" y={4} fontSize={11} fontWeight={600} fill={colour}>{text}</text>
    </g>
  );
}

/**
 * Props beyond the scene: `onCardClick(card)` and `onLoopClick(loop)` hand a tap to
 * the map's handlers (SceneMap maps a scene id to the row they expect); `litIds` are
 * cards drawn with a white edge (the map's selection). The ref exposes PathwayCanvas's
 * API plus `zoomToNode(id)` and `getNodePosition(id)` from the scene's cards, so the
 * map view drives this renderer exactly as it drove the layered one.
 */
export const SceneRenderer = memo(forwardRef(function SceneRenderer({
  scene, width, height, onZoomChange = null, onHoverCard = null, onCardClick = null, onLoopClick = null, litIds = null,
  // A tap on a connector's label (its type, to change) and on a link or association
  // (a connection, to retype); `overlay` is drawn last: the write affordances.
  onEdgeLabelClick = null, onEdgeClick = null, overlay = null,
  underlay = null, // drawn under the connectors and cards: the choice boxes
}, ref) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [active, setActive] = useState(null); // a hovered or tapped edge: its association label shows
  const [selected, setSelected] = useState(null);
  const shown = selected || active;
  const cardById = useMemo(() => new Map(scene.cards.map((c) => [c.id, c])), [scene.cards]);
  // A rebuilt scene (a card added, a link drawn) must not yank the view: the canvas
  // auto-fits on new bounds after an idle spell, so a rebuild counts as fresh activity,
  // as the layered renderer's edits did. The first scene still fits on mount.
  const firstScene = useRef(true);
  useEffect(() => {
    if (firstScene.current) { firstScene.current = false; return; }
    canvasRef.current?.markInteraction?.();
  }, [scene]);
  useImperativeHandle(ref, () => ({
    fitNow: (animate) => canvasRef.current?.fitNow(animate),
    panBy: (dx, dy, animate) => canvasRef.current?.panBy?.(dx, dy, animate),
    panToPoint: (svgX, svgY, screenX, screenY, animate) => canvasRef.current?.panToPoint(svgX, svgY, screenX, screenY, animate),
    getTransform: () => canvasRef.current?.getTransform?.(),
    setTransform: (tr, animate) => canvasRef.current?.setTransform?.(tr, animate),
    markUserInteraction: () => canvasRef.current?.markInteraction?.(),
    zoomToPoint: (svgX, svgY, scale, animate) => canvasRef.current?.zoomToPoint(svgX, svgY, scale, animate),
    zoomToNode: (id, scale = 1.0, animate = true) => {
      const c = cardById.get(id);
      if (!c) return false;
      canvasRef.current?.zoomToPoint(c.cx, c.cy, scale, animate);
      return true;
    },
    getNodePosition: (id) => { const c = cardById.get(id); return c ? { x: c.x, y: c.y } : null; },
  }), [cardById]);
  const behind = scene.edges.filter((e) => e.behind);
  const front = scene.edges.filter((e) => !e.behind);
  const stubColour = (e) => (e.blockers.length && scene.cards.find((c) => c.id === e.blockers[0])?.kind === 'avoider' ? COLOURS.avoider : COLOURS.blocker);
  const colours = [...new Set([...front.map(edgeColour), COLOURS.blocker, COLOURS.avoider, EFFECT_TYPE_COLORS.leads_to, ...behind.map(edgeColour)])];
  const { cardWidth, cardHeight } = scene.config;
  const b = scene.bounds;
  // PathwayCanvas adds one card's size to the bounds it is given.
  const contentBounds = useMemo(() => ({ minX: b.minX, minY: b.minY, maxX: b.maxX - cardWidth, maxY: b.maxY - cardHeight }), [b, cardWidth, cardHeight]);
  const edgeLabel = (e) => (e.type ? t(`impactPath.scene.edge.${e.type}`) : null);
  const edgeById = new Map(scene.edges.map((e) => [e.id, e]));
  const cardEnds = new Set(scene.cards.map((c) => `${c.x},${c.cy}`));
  const endsAtCard = (pts) => cardEnds.has(`${pts[pts.length - 1].x},${pts[pts.length - 1].y}`);
  // The cards do not change with hover or selection: built once per scene, so
  // passing over a line does not re-render 250 HTML cards under the zoom.
  const cardLayer = useMemo(() => scene.cards.map((c) => (
    <SceneCard key={c.id} card={c} t={t} onHover={onHoverCard} onClick={onCardClick} lit={Boolean(litIds?.has(c.id))} />
  )), [scene.cards, t, onHoverCard, onCardClick, litIds]);
  // A tap on a connection (a link or an association, never the tree) also reaches the
  // map: that is where its type is changed. A tree connector's type lives on its label.
  const hit = (e) => ({
    onMouseEnter: () => setActive(e.id),
    onMouseLeave: () => setActive(null),
    onClick: (ev) => {
      ev.stopPropagation();
      setSelected(e.id === selected ? null : e.id);
      if (onEdgeClick && e.kind !== 'primary' && e.kind !== 'structural' && !e.synthetic) onEdgeClick(e, { x: ev.clientX, y: ev.clientY });
    },
  });
  // A label shared by several connectors of one type names no single one to retype: the line does.
  const labelClick = (e) => (onEdgeLabelClick && e.kind !== 'structural' && !e.synthetic && !(e.labelGroup > 1) ? (pos) => onEdgeLabelClick(e, pos) : null);

  return (
    <PathwayCanvas ref={canvasRef} width={width} height={height} contentBounds={contentBounds} cardWidth={cardWidth} cardHeight={cardHeight} onZoomChange={onZoomChange} onBackgroundClick={() => setSelected(null)}>
      <defs>
        {colours.map((c) => (
          <marker key={c} id={markerIdFor(c)} viewBox="0 0 10 10" refX="9" refY="5" markerWidth={8} markerHeight={8} orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
          </marker>
        ))}
      </defs>
      <g className="scene-lanes">
        {scene.lanes.map((l, i) => (
          <rect key={l.id} x={l.left - 40} y={l.top - 40} width={l.right - l.left + 80} height={l.bottom - l.top + 80} rx={24} fill={i % 2 ? '#ffffff05' : '#ffffff0a'} />
        ))}
      </g>
      {underlay}
      <g className="scene-behind" opacity={0.5}>
        {scene.loops.filter((l) => l.path).map((l) => (
          <g key={l.id} style={onLoopClick ? { cursor: 'pointer' } : undefined} onClick={onLoopClick && ((ev) => { ev.stopPropagation(); onLoopClick(l); })}>
            <path d={l.path} fill="none" stroke="transparent" strokeWidth={14} />
            <path d={l.path} fill="none" stroke={COLOURS.milestone} strokeWidth={1.5} strokeDasharray="4 6" />
          </g>
        ))}
        {behind.map((e) => (
          <g key={e.id} {...hit(e)}>
            <path d={e.path} fill="none" stroke="transparent" strokeWidth={14} />
            <path d={e.path} fill="none" stroke={edgeColour(e)} strokeWidth={shown === e.id ? 3 : 1.5} markerEnd={`url(#${markerIdFor(edgeColour(e))})`} />
          </g>
        ))}
      </g>
      <g className="scene-front">
        {/* The ink: every connector one line, card to card, shifted a pixel aside where it
            shares a run with others so they lie side by side as a ribbon (sceneInk.js). */}
        {front.map((e) => {
          const d = e.draw || { path: e.path, points: e.points };
          const colour = edgeColour(e);
          return (
            <g key={e.id} {...hit(e)} opacity={selected && selected !== e.id ? 0.25 : 1}>
              <path d={e.path} fill="none" stroke="transparent" strokeWidth={16} />
              <path d={d.path} fill="none" stroke={colour} strokeWidth={e.kind === 'structural' ? 2 : 3} strokeDasharray={e.state === 'proposed' ? '7 5' : undefined} markerEnd={e.blockers.length || !endsAtCard(e.points) ? undefined : `url(#${markerIdFor(colour)})`} />
              {e.stubs.map((s, i) => (
                <path key={i} d={s.path} fill="none" stroke={stubColour(e)} strokeWidth={2} strokeDasharray="6 5" markerEnd={i === e.stubs.length - 1 ? `url(#${markerIdFor(stubColour(e))})` : undefined} />
              ))}
            </g>
          );
        })}
        {/* the selected connector once more, on top of the ribbon it is part of */}
        {front.filter((e) => e.id === selected).map((e) => (
          <path key={`sel-${e.id}`} d={(e.draw || e).path} fill="none" stroke={edgeColour(e)} strokeWidth={3} strokeDasharray={e.state === 'proposed' ? '7 5' : undefined} pointerEvents="none" />
        ))}
      </g>
      <g className="scene-cards">
        {cardLayer}
      </g>
      <g className="scene-labels">
        {front.filter((e) => e.knot && e.kind === 'primary' && !e.bundle).map((e) => (
          <circle key={`k-${e.id}`} cx={e.knot.x} cy={e.knot.y} r={scene.config.knotRadius / 2} fill={e.blockers.length ? COLOURS.blocker : edgeColour(e)} pointerEvents="none" />
        ))}
        {/* The type once per source and type, on the run those connectors share (sceneLabels.js). */}
        {front.filter((e) => e.label && e.labelLead && edgeLabel(e)).map((e) => (
          <EdgeLabel key={`l-${e.id}`} point={e.label} text={edgeLabel(e)} colour={edgeColour(e)} onClick={labelClick(e)} />
        ))}
        {scene.bundles.flatMap((bundle) => bundle.labels.filter((l) => l.label && !front.some((e) => e.bundle === bundle.id && e.labelLead !== undefined)).map((l) => {
          const member = bundle.members.map((id) => edgeById.get(id)).find((e) => e && e.to === l.to);
          return (
            <EdgeLabel key={`bl-${bundle.id}-${l.to}`} point={l.label} text={edgeLabel(l)} colour={EFFECT_TYPE_COLORS[l.type] || EFFECT_TYPE_COLORS.leads_to} onClick={member ? labelClick(member) : null} />
          );
        }))}
        {behind.filter((e) => e.id === shown && e.label).map((e) => (
          <EdgeLabel key={`l-${e.id}`} point={e.label} text={edgeLabel(e)} colour="#c8cfdc" onClick={labelClick(e)} />
        ))}
      </g>
      {overlay}
    </PathwayCanvas>
  );
}));

/** Loads the map graph for an instance and draws its scene, with the timeline axis under it. */
export default function SceneView({ instanceId }) {
  const { t } = useTranslation();
  const [graph, setGraph] = useState(null);
  const [error, setError] = useState(null);
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!instanceId) return undefined;
    let cancelled = false;
    fetch(API_ENDPOINTS.MAP_GRAPH(instanceId), { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body?.error?.message || body?.error || `HTTP ${r.status}`);
        return body;
      })
      .then((g) => { if (!cancelled) { setGraph(g); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [instanceId]);

  const scene = useMemo(() => (graph ? buildScene(graph) : null), [graph]);
  const times = useMemo(() => cardTimes(graph), [graph]);
  const rounds = useMemo(() => reportRounds(graph), [graph]);

  // The axis follows the zoom at most every 50 ms; the map itself does not
  // re-render. A timer, not requestAnimationFrame, which a hidden tab pauses.
  const [transform, setTransform] = useState(null);
  const [hovered, setHovered] = useState(null);
  const pending = useRef(null);
  const timer = useRef(null);
  const onZoomChange = useCallback((tr) => {
    pending.current = { k: tr.k, x: tr.x, y: tr.y };
    if (timer.current) return;
    timer.current = setTimeout(() => { timer.current = null; setTransform(pending.current); }, 50);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  if (error) return <Notice>{t('impactPath.scene.error', { message: error })}</Notice>;
  if (!scene) return <Notice>{t('impactPath.scene.loading')}</Notice>;
  if (!scene.cards.length) return <Notice>{t('impactPath.scene.empty')}</Notice>;
  return (
    <Frame>
      <SceneRenderer scene={scene} width={size.width} height={size.height - AXIS_HEIGHT} onZoomChange={onZoomChange} onHoverCard={setHovered} />
      <TimelineAxis cards={scene.cards} times={times} rounds={rounds} transform={transform} width={size.width} height={size.height} hovered={hovered} />
    </Frame>
  );
}

const Frame = styled.div`
  position: fixed; inset: 0; background: #0f121a; color: #e8ebf2; overflow: hidden;
`;

const Notice = styled.div`
  position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
  background: #0f121a; color: #c8cfdc; font-size: 16px; padding: 24px; text-align: center;
`;
